import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Kanban as KanbanIcon, Table as TableIcon, ShieldCheck, ListChecks,
} from '@phosphor-icons/react';
import {
  DndContext, PointerSensor, useSensor, useSensors, pointerWithin,
  DragOverlay, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { useAppStore } from '@/store/appStore';
import { useCatalogs, useTasks, useMoveTask } from '@/hooks/useTasks';
import { ESTADO_IDS } from '@/constants/estadoIds';
import { KanbanColumn } from '@/components/kanban/KanbanColumn';
import { contrastingText } from '@/lib/utils';
import type { Task, Status } from '@/types';

// Tareas personales de un colaborador (se llega clickeando una tarjeta del
// Dashboard de equipo). Dos grupos:
//   Revisiones (es el Autorizado Interno): Por Revisar / Revisadas
//   Asignadas (es el Responsable): una sección por cada estado con tareas
// Toggle Tabla / Kanban manteniendo el mismo agrupado. Alcance: el sprint
// seleccionado (filters.sprintId, fallback al semanal vigente).

type ViewMode = 'table' | 'kanban';

// IDs sintéticos para las columnas de Revisiones en el kanban (no son estados
// reales, así que no son destino de drag de cambio de estado).
const COL_POR_REVISAR = -101;
const COL_REVISADAS = -102;
// Base para ids sintéticos de las columnas "en curso" de Revisiones
// (-200 - idEstado). Negativos → nunca chocan con estados reales ni son
// destino de drag (handleDragEnd solo acepta ids de estado válidos).
const COL_REV_BASE = -200;

export function PersonalTasksPage() {
  const { idCol: idColParam } = useParams<{ idCol: string }>();
  const idCol = Number(idColParam);
  const navigate = useNavigate();

  const sprintIdFilter = useAppStore((s) => s.filters.sprintId);
  const setFilter = useAppStore((s) => s.setFilter);
  const selectTask = useAppStore((s) => s.selectTask);
  const setPendingStatusChange = useAppStore((s) => s.setPendingStatusChange);

  const { data: catalogs } = useCatalogs();
  const { data: tasks = [] } = useTasks('status', '');
  const moveTask = useMoveTask();

  const [view, setView] = useState<ViewMode>('table');
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const user = catalogs?.users.find((u) => u.idCol === idCol);
  const today = new Date().toISOString().slice(0, 10);

  // Sprint efectivo: el del filtro, o el semanal vigente como fallback.
  const vigente = catalogs?.sprints.find(
    (s) => s.tipo === 'Semanal' && s.activo
      && s.fechaInicio && s.fechaFin
      && s.fechaInicio.slice(0, 10) <= today && s.fechaFin.slice(0, 10) >= today,
  );
  const effectiveSprintId = sprintIdFilter ?? vigente?.idSprint ?? null;

  // Tareas activas de la persona en el sprint efectivo.
  const { revisionTasks, asignadasTasks } = useMemo(() => {
    const inSprint = tasks.filter(
      (t) => t.esActivo !== false && (effectiveSprintId == null || t.idSprint === effectiveSprintId),
    );
    return {
      revisionTasks: inSprint.filter((t) => t.idAutorizadoInt === idCol),
      asignadasTasks: inSprint.filter((t) => t.responsable === idCol),
    };
  }, [tasks, effectiveSprintId, idCol]);

  // Estados ordenados (para las secciones de Asignadas + colores).
  const statuses = useMemo(
    () => [...(catalogs?.statuses ?? [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [catalogs?.statuses],
  );
  const statusById = useMemo(() => {
    const m = new Map<number, Status>();
    statuses.forEach((s) => m.set(s.idEst, s));
    return m;
  }, [statuses]);
  // "Por Revisar" (lo que YO reviso) en rojo de marca para que resalte como
  // pendiente de acción. "Revisadas" en verde (Completado).
  const colorPorRevisar = '#BB4A4A'; // brand-red-200
  const colorRevisadas = statusById.get(ESTADO_IDS.COMPLETADO)?.colorHex ?? '#1D9E75';

  // Secciones de Revisiones — muestran TODAS las tareas donde es revisor
  // (revisionTasks), para que coincida con el total "como revisor":
  //   - "Por Revisar": estado Revisión (acción pendiente ahora).
  //   - en curso: una sección por cada otro estado, sin fecha de autorización
  //     (las que revisará más adelante mientras el responsable trabaja).
  //   - "Revisadas": con fecha de autorización (ya firmadas).
  const porRevisar = useMemo(
    () => revisionTasks.filter((t) => t.idEstado === ESTADO_IDS.REVISION),
    [revisionTasks],
  );
  const revisadas = useMemo(
    () => revisionTasks.filter((t) => t.fechaAutorizadoInt != null && t.idEstado !== ESTADO_IDS.REVISION),
    [revisionTasks],
  );
  const revisionEnCurso = useMemo(
    () => statuses
      .filter((st) => st.idEst !== ESTADO_IDS.REVISION)
      .map((st) => ({
        status: st,
        tasks: revisionTasks.filter((t) => t.idEstado === st.idEst && t.fechaAutorizadoInt == null),
      }))
      .filter((s) => s.tasks.length > 0),
    [statuses, revisionTasks],
  );

  // Secciones de Asignadas: una por estado con tareas, en orden. La columna
  // del estado Revisión se muestra SIEMPRE (aunque esté vacía) para que quede
  // explícito dónde caen las tareas propias en revisión, separadas del "Por
  // Revisar" (lo que uno revisa). El resto de estados vacíos se ocultan.
  const asignadasSecciones = useMemo(
    () => statuses
      .map((st) => ({ status: st, tasks: asignadasTasks.filter((t) => t.idEstado === st.idEst) }))
      .filter((s) => s.tasks.length > 0 || s.status.idEst === ESTADO_IDS.REVISION),
    [statuses, asignadasTasks],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function handleDragStart(e: DragStartEvent) {
    const t = e.active.data.current?.task as Task | undefined;
    if (t) setActiveTask(t);
  }

  // Solo las columnas de estado (Asignadas) aceptan drop = cambio de estado.
  // Mismo patrón que el kanban normal: si el estado destino requiere
  // FechaCompletado y la tarea no la tiene, abrimos el modal con el cambio
  // pendiente en vez de mutar a ciegas.
  function handleDragEnd(e: DragEndEvent) {
    setActiveTask(null);
    const { active, over } = e;
    if (!over) return;
    const task = active.data.current?.task as Task | undefined;
    if (!task) return;

    let targetEstado: number | null = null;
    if (over.data.current?.type === 'column') targetEstado = over.data.current.groupId as number;
    else if (over.data.current?.type === 'task') targetEstado = (over.data.current.task as Task).idEstado ?? null;

    // Ignorar drops en columnas que no son estados reales (Revisiones).
    if (targetEstado == null || !statusById.has(targetEstado)) return;
    if (task.idEstado === targetEstado) return;

    const targetStatus = statusById.get(targetEstado);
    if (targetStatus?.requiresFechaCompletado && !task.fechaCompletado) {
      setPendingStatusChange({ taskId: task.idTareaArq, newStatusId: targetEstado });
      selectTask(task.idTareaArq);
      return;
    }
    moveTask.mutate({
      id: task.idTareaArq,
      dto: { groupBy: 'status', targetGroupId: targetEstado, rowVersion: task.rowVersion ?? undefined },
    });
  }

  if (!idColParam || Number.isNaN(idCol)) {
    return <div className="p-8 text-stone-500">Colaborador inválido.</div>;
  }

  return (
    <div className="flex h-full flex-col bg-brand-bg">
      {/* Barra de controles */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3 border-b border-stone-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          title="Volver al Dashboard"
          className="flex items-center gap-1 rounded-md border border-stone-300 px-2 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>

        {/* Selector de colaborador: ver las tareas personales de otro. */}
        <select
          value={idCol}
          onChange={(e) => navigate(`/personal/${e.target.value}`)}
          title="Ver tareas de"
          className="cursor-pointer rounded-md border border-stone-300 bg-white px-2 py-1 text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-green-100 max-w-[220px]"
        >
          {!user && <option value={idCol}>Colaborador #{idCol}</option>}
          {(catalogs?.users ?? []).map((u) => (
            <option key={u.idCol} value={u.idCol}>{u.nombreCompleto || u.nombre || `#${u.idCol}`}</option>
          ))}
        </select>

        {/* Selector de sprint (mismo estado global) */}
        {catalogs && catalogs.sprints.length > 0 && (
          <select
            value={sprintIdFilter ?? ''}
            onChange={(e) => setFilter('sprintId', e.target.value ? Number(e.target.value) : null)}
            title="Sprint"
            className="cursor-pointer rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-semibold text-stone-700 focus:outline-none focus:ring-2 focus:ring-brand-green-100 max-w-[200px]"
          >
            <option value="">— Sprint vigente —</option>
            {catalogs.sprints.map((s) => (
              <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
            ))}
          </select>
        )}

        {/* Toggle Tabla / Kanban */}
        <div className="ml-auto flex shrink-0 items-stretch overflow-hidden rounded-md border border-stone-300">
          <button
            type="button"
            onClick={() => setView('table')}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              view === 'table' ? 'bg-brand-green-100 text-black' : 'text-stone-600 hover:bg-stone-100'
            }`}
          >
            <TableIcon size={14} weight="bold" /> Tabla
          </button>
          <button
            type="button"
            onClick={() => setView('kanban')}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              view === 'kanban' ? 'bg-brand-green-100 text-black' : 'text-stone-600 hover:bg-stone-100'
            }`}
          >
            <KanbanIcon size={14} weight="bold" /> Kanban
          </button>
        </div>
      </div>

      {view === 'table' ? (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          <GroupBand icon={<ShieldCheck size={15} weight="bold" />} title="Revisiones" subtitle="Tareas donde sos el revisor" />
          <SectionTable label="Por Revisar" color={colorPorRevisar} tasks={porRevisar} onRow={selectTask} />
          {revisionEnCurso.map((s) => (
            <SectionTable
              key={s.status.idEst}
              label={s.status.estado || `#${s.status.idEst}`}
              color={s.status.colorHex ?? '#888780'}
              tasks={s.tasks}
              onRow={selectTask}
            />
          ))}
          <SectionTable label="Revisadas" color={colorRevisadas} tasks={revisadas} onRow={selectTask} />
          {porRevisar.length === 0 && revisadas.length === 0 && revisionEnCurso.length === 0 && (
            <p className="px-1 text-xs italic text-stone-400">No sos revisor de ninguna tarea en este sprint.</p>
          )}

          <GroupBand icon={<ListChecks size={15} weight="bold" />} title="Asignadas" subtitle="Tareas donde sos el responsable" />
          {asignadasSecciones.map((s) => (
            <SectionTable
              key={s.status.idEst}
              label={s.status.estado || `#${s.status.idEst}`}
              color={s.status.colorHex ?? '#888780'}
              tasks={s.tasks}
              onRow={selectTask}
            />
          ))}
          {asignadasSecciones.length === 0 && (
            <p className="px-1 text-xs italic text-stone-400">Sin tareas asignadas.</p>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {/* Todas las columnas en una sola fila. Los grupos Revisiones /
              Asignadas van lado a lado (cada uno con su rótulo encima de sus
              columnas) separados por una línea, no apilados. */}
          <div className="flex-1 overflow-auto p-3">
            <div className="flex items-start gap-4">
              {/* Revisiones (columnas display-only) */}
              <div className="flex flex-col gap-2">
                <GroupBand icon={<ShieldCheck size={15} weight="bold" />} title="Revisiones" subtitle="sos el revisor" />
                <div className="flex gap-3">
                  <KanbanColumn groupId={COL_POR_REVISAR} groupName="Por Revisar" groupColor={colorPorRevisar} tasks={porRevisar} />
                  {revisionEnCurso.map((s) => (
                    <KanbanColumn
                      key={`rev-${s.status.idEst}`}
                      groupId={COL_REV_BASE - s.status.idEst}
                      groupName={s.status.estado || `#${s.status.idEst}`}
                      groupColor={s.status.colorHex ?? '#888780'}
                      tasks={s.tasks}
                    />
                  ))}
                  <KanbanColumn groupId={COL_REVISADAS} groupName="Revisadas" groupColor={colorRevisadas} tasks={revisadas} />
                </div>
              </div>

              <div className="self-stretch w-px bg-stone-300" />

              {/* Asignadas (columnas por estado, drag cambia de estado) */}
              <div className="flex flex-col gap-2">
                <GroupBand icon={<ListChecks size={15} weight="bold" />} title="Asignadas" subtitle="arrastrá para cambiar de estado" />
                <div className="flex gap-3">
                  {asignadasSecciones.map((s) => (
                    <KanbanColumn
                      key={s.status.idEst}
                      groupId={s.status.idEst}
                      groupName={s.status.estado || `#${s.status.idEst}`}
                      groupColor={s.status.colorHex ?? '#888780'}
                      tasks={s.tasks}
                    />
                  ))}
                  {asignadasSecciones.length === 0 && (
                    <p className="px-1 py-4 text-xs italic text-stone-400">Sin tareas asignadas.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DragOverlay>{activeTask ? <div className="opacity-80"><MiniCard task={activeTask} /></div> : null}</DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

// Banda de título de grupo (Revisiones / Asignadas).
function GroupBand({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-stone-200 pb-1">
      <span className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-brand-gray-500">
        {icon}{title}
      </span>
      {subtitle && <span className="text-[11px] text-stone-400">· {subtitle}</span>}
    </div>
  );
}

// Tabla liviana por sección. Cabecera con chip de estado + conteo, filas
// clickeables que abren el TaskModal.
function SectionTable({
  label, color, tasks, onRow,
}: {
  label: string;
  color: string;
  tasks: Task[];
  onRow: (id: number) => void;
}) {
  if (tasks.length === 0) return null;
  const txt = contrastingText(color);
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: color, color: txt }}>
          {label}
        </span>
        <span className="text-xs text-stone-400">{tasks.length}</span>
      </div>
      <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-[11px] uppercase text-stone-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Tarea</th>
              <th className="w-32 px-3 py-2 text-left font-medium">Proyecto</th>
              <th className="w-40 px-3 py-2 text-left font-medium">Resp. / Revisor</th>
              <th className="w-20 px-3 py-2 text-right font-medium">Horas</th>
              <th className="w-28 px-3 py-2 text-left font-medium">F. proy.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {tasks.map((t) => (
              <tr
                key={t.idTareaArq}
                onClick={() => onRow(t.idTareaArq)}
                className="cursor-pointer hover:bg-stone-50"
              >
                <td className="px-3 py-2 text-stone-800">{t.nombreTarea || `#${t.idTareaArq}`}</td>
                <td className="px-3 py-2">
                  {t.nomProyecto && (
                    <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-500">{t.nomProyecto}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-stone-600">
                  {t.nomResponsable || '—'}
                  {t.nomAutorizado && <span className="text-stone-400"> · rev: {t.nomAutorizado}</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-stone-600">
                  {t.horasEstimadas != null ? `${t.horasEstimadas}h` : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-stone-500">
                  {t.fechaProyectada ? t.fechaProyectada.slice(0, 10) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Card mínima para el DragOverlay del kanban.
function MiniCard({ task }: { task: Task }) {
  return (
    <div className="w-[240px] rounded-md border border-stone-200 bg-white px-2.5 py-2 shadow-lg">
      <div className="line-clamp-2 text-xs font-medium text-stone-800">{task.nombreTarea || `#${task.idTareaArq}`}</div>
      <div className="mt-1 flex items-center gap-1.5">
        {task.nomEstado && (
          <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: task.colorEstado ?? '#888780' }}>
            {task.nomEstado}
          </span>
        )}
        {task.nomProyecto && <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-500">{task.nomProyecto}</span>}
      </div>
    </div>
  );
}
